import { FC } from 'react';
import { Box, VStack, HStack, Text, Badge } from '@chakra-ui/react';
import { PedigreeTree as PedigreeTreeType, PedigreeNode } from '@/types/pedigree';
import { DogIdentityLabel } from '@/components/dog/DogIdentityLabel';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface PedigreeTreeProps {
  pedigree: PedigreeTreeType;
}

/**
 * 血統図表示コンポーネント（3世代表示）
 */
export const PedigreeTree: FC<PedigreeTreeProps> = ({ pedigree }) => {
  /**
   * 犬の情報を表示するコンポーネント
   */
  const DogInfo: FC<{ node: PedigreeNode | null; label: string }> = ({ node, label }) => {
    if (!node || !node.dog) {
      return (
        <Box p={3} borderWidth={1} borderRadius="md" bg="gray.50" borderColor="gray.200">
          <Text fontSize="sm" color="gray.500">
            {label}: 不明
          </Text>
        </Box>
      );
    }

    const dog = node.dog;

    return (
      <Box
        p={3}
        borderWidth={1}
        borderRadius="md"
        bg="white"
        borderColor="gray.300"
        _hover={{ borderColor: 'blue.400', shadow: 'sm' }}
      >
        <VStack align="stretch" gap={1}>
          <HStack justify="space-between">
            <Text fontWeight="bold" fontSize="sm">
              {label}
            </Text>
            <Badge colorPalette={dog.gender === 'male' ? 'blue' : 'pink'} fontSize="xs">
              {dog.gender === 'male' ? 'オス' : 'メス'}
            </Badge>
          </HStack>
          <DogIdentityLabel dog={dog} size="sm" showRegistrationNumbers />
          <Text fontSize="xs" color="gray.500">
            {format(dog.birthDate.toDate(), 'yyyy/M/d', { locale: ja })}
          </Text>
        </VStack>
      </Box>
    );
  };

  return (
    <Box>
      {/* テーブル形式の血統図 */}
      <VStack align="stretch" gap={4}>
        {/* 本犬 */}
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">
            本犬
          </Text>
          <Box p={4} borderWidth={2} borderRadius="md" bg="blue.50" borderColor="blue.300">
            <VStack align="stretch" gap={1}>
              <HStack justify="space-between">
                <DogIdentityLabel
                  dog={pedigree.subject}
                  size="lg"
                  showRegistrationNumbers
                />
                <Badge
                  colorPalette={pedigree.subject.gender === 'male' ? 'blue' : 'pink'}
                  fontSize="sm"
                >
                  {pedigree.subject.gender === 'male' ? 'オス' : 'メス'}
                </Badge>
              </HStack>
              <Text fontSize="sm" color="gray.600">
                {format(pedigree.subject.birthDate.toDate(), 'yyyy年M月d日', { locale: ja })}生
              </Text>
            </VStack>
          </Box>
        </Box>

        {/* 両親（第1世代） */}
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">
            両親
          </Text>
          <HStack gap={4}>
            <Box flex={1}>
              <DogInfo node={pedigree.father} label="父" />
            </Box>
            <Box flex={1}>
              <DogInfo node={pedigree.mother} label="母" />
            </Box>
          </HStack>
        </Box>

        {/* 祖父母（第2世代） */}
        {(pedigree.father || pedigree.mother) && (
          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">
              祖父母
            </Text>
            <HStack gap={4}>
              {/* 父方の祖父母 */}
              <VStack flex={1} gap={2}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                  父方
                </Text>
                <DogInfo
                  node={
                    pedigree.father?.father
                      ? { dog: pedigree.father.father.dog, father: undefined, mother: undefined }
                      : null
                  }
                  label="祖父"
                />
                <DogInfo
                  node={
                    pedigree.father?.mother
                      ? { dog: pedigree.father.mother.dog, father: undefined, mother: undefined }
                      : null
                  }
                  label="祖母"
                />
              </VStack>

              {/* 母方の祖父母 */}
              <VStack flex={1} gap={2}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                  母方
                </Text>
                <DogInfo
                  node={
                    pedigree.mother?.father
                      ? { dog: pedigree.mother.father.dog, father: undefined, mother: undefined }
                      : null
                  }
                  label="祖父"
                />
                <DogInfo
                  node={
                    pedigree.mother?.mother
                      ? { dog: pedigree.mother.mother.dog, father: undefined, mother: undefined }
                      : null
                  }
                  label="祖母"
                />
              </VStack>
            </HStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};
